import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Save,
  Plus,
  X,
  Settings,
  Package,
  DollarSign,
  Target,
  LayoutTemplate,
  Palette,
  Monitor,
  Megaphone,
  BookOpen,
  PenTool,
  Video,
  Sparkles
} from 'lucide-react';
import { services } from '@/data/projects';
import { PremiumServiceCard } from '@/components/PremiumServiceCard';

const serviceSchema = z.object({
  name: z.string().min(1, 'Nome do serviço é obrigatório'),
  summary: z.string().min(10, 'Resumo deve ter pelo menos 10 caracteres'),
  deliverables: z.array(z.string()).min(1, 'Selecione pelo menos uma entrega'),
  process: z.array(z.string()).min(1, 'Selecione pelo menos uma etapa do processo'),
  startingPrice: z.string().optional(),
});

type ServiceFormData = z.infer<typeof serviceSchema>;

const ServicoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [newDeliverable, setNewDeliverable] = useState('');
  const [newProcessStep, setNewProcessStep] = useState('');

  const service = isEditing ? services.find(s => s.id === id) : null;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ServiceFormData>({
    resolver: zodResolver(serviceSchema),
    defaultValues: service || {
      name: '',
      summary: '',
      deliverables: [],
      process: [],
      startingPrice: '',
    },
  });

  const watchedValues = watch();

  // Helper for preview icon
  const getServiceIcon = (name: string = '') => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('branding') || lowerName.includes('identidade')) return Palette;
    if (lowerName.includes('ui/ux') || lowerName.includes('interface') || lowerName.includes('web')) return Monitor;
    if (lowerName.includes('direção') || lowerName.includes('campanha') || lowerName.includes('marketing')) return Megaphone;
    if (lowerName.includes('editorial') || lowerName.includes('diagramação')) return BookOpen;
    if (lowerName.includes('ilustração') || lowerName.includes('arte')) return PenTool;
    if (lowerName.includes('motion') || lowerName.includes('vídeo') || lowerName.includes('video')) return Video;
    return Sparkles;
  };

  const onSubmit = async (data: ServiceFormData) => {
    setIsLoading(true);
    try {
      console.log('Salvando serviço:', data);
      await new Promise(resolve => setTimeout(resolve, 1000));
      navigate('/admin/servicos');
    } catch (error) {
      console.error('Erro ao salvar serviço:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addItem = (field: 'deliverables' | 'process', value: string, setter: (value: string) => void) => {
    if (value.trim()) {
      const currentValues = watchedValues[field] || [];
      setValue(field, [...currentValues, value.trim()]);
      setter('');
    }
  };

  const removeItem = (field: 'deliverables' | 'process', index: number) => {
    const currentValues = watchedValues[field] || [];
    setValue(field, currentValues.filter((_, i) => i !== index));
  };

  const renderArrayField = (
    field: 'deliverables' | 'process',
    label: string,
    placeholder: string,
    value: string,
    setter: (value: string) => void,
    icon: React.ComponentType<{ className?: string }>
  ) => {
    const Icon = icon;
    const currentValues = watchedValues[field] || [];

    return (
      <div className="space-y-3">
        <Label>{label}</Label>
        <div className="flex space-x-2">
          <div className="relative flex-1">
            <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={placeholder}
              value={value}
              onChange={(e) => setter(e.target.value)}
              className="pl-10"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem(field, value, setter))}
            />
          </div>
          <Button
            type="button"
            onClick={() => addItem(field, value, setter)}
            disabled={!value.trim()}
            variant="secondary"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        {currentValues.length > 0 ? (
          <div className="flex flex-wrap gap-2 mt-3 p-4 bg-muted/30 rounded-lg border border-border/50">
            {currentValues.map((item, index) => (
              <Badge key={index} variant="secondary" className="pl-2 pr-1 py-1 flex items-center gap-1 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <span>{item}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 ml-1 hover:bg-red-100 hover:text-red-600 rounded-full"
                  onClick={() => removeItem(field, index)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground pl-1 italic">Nenhum item adicionado ainda.</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {/* Premium Sticky Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md pb-4 pt-2 border-b mb-6 -mx-6 px-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/servicos')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEditing ? 'Editar Serviço' : 'Novo Serviço'}
            </h1>
            <p className="text-muted-foreground hidden md:block">
              {isEditing ? 'Atualize as informações do serviço' : 'Crie um novo serviço para oferecer'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleSubmit(onSubmit)} disabled={isLoading} className="shadow-lg bg-blue-600 hover:bg-blue-700 transition-all">
            <Save className="w-4 h-4 mr-2" />
            {isLoading ? 'Salvando...' : 'Salvar Serviço'}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

          {/* Main Content */}
          <div className="xl:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Settings className="w-5 h-5 text-blue-500" />
                  <span>Informações Básicas</span>
                </CardTitle>
                <CardDescription>
                  Dados principais e descrição do serviço
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Serviço *</Label>
                  <Input
                    id="name"
                    {...register('name')}
                    placeholder="Ex: Identidade Visual Corporativa"
                    className="text-lg font-medium"
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive">{errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="summary">Resumo do Serviço *</Label>
                  <Textarea
                    id="summary"
                    {...register('summary')}
                    placeholder="Descreva brevemente o serviço, benefícios e resultados esperados..."
                    rows={4}
                    className="resize-none"
                  />
                  {errors.summary && (
                    <p className="text-sm text-destructive">{errors.summary.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground text-right">
                    {watchedValues.summary?.length || 0} caracteres
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Package className="w-5 h-5 text-blue-500" />
                  <span>O que está incluso</span>
                </CardTitle>
                <CardDescription>
                  Lista de entregáveis para o cliente
                </CardDescription>
              </CardHeader>
              <CardContent>
                {renderArrayField(
                  'deliverables',
                  'Entregas *',
                  'Adicionar entrega (ex: Manual de Marca PDF)',
                  newDeliverable,
                  setNewDeliverable,
                  Package
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Target className="w-5 h-5 text-blue-500" />
                  <span>Processo de Trabalho</span>
                </CardTitle>
                <CardDescription>
                  Etapas do desenvolvimento
                </CardDescription>
              </CardHeader>
              <CardContent>
                {renderArrayField(
                  'process',
                  'Etapas do Processo *',
                  'Adicionar etapa (ex: Briefing e Imersão)',
                  newProcessStep,
                  setNewProcessStep,
                  Target
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">

            {/* Status & Price Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-blue-500" />
                  <span>Precificação</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="startingPrice">Preço Inicial</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="startingPrice"
                      {...register('startingPrice')}
                      placeholder="Sob consulta"
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Exibido como "A partir de..." no card
                  </p>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Status</p>
                    <p className="text-sm font-semibold text-green-600 flex items-center justify-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span> Ativo
                    </p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Atualizado</p>
                    <p className="text-sm">Hoje</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview Card */}
            <Card className="bg-slate-950 border-slate-800 overflow-hidden">
              <CardHeader className="border-b border-slate-800 pb-4">
                <CardTitle className="flex items-center space-x-2 text-white">
                  <LayoutTemplate className="w-5 h-5 text-blue-400" />
                  <span>Live Preview</span>
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Como aparecerá na página de Serviços
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 bg-slate-900 flex justify-center items-center min-h-[400px]">
                <div className="w-full max-w-[350px] transform scale-90 sm:scale-100 transition-transform">
                  <PremiumServiceCard
                    index={0}
                    title={watchedValues.name || 'Nome do Serviço'}
                    description={watchedValues.summary || 'Resumo do serviço aparecerá aqui...'}
                    price={watchedValues.startingPrice || 'Sob consulta'}
                    icon={getServiceIcon(watchedValues.name)}
                    deliverables={watchedValues.deliverables && watchedValues.deliverables.length > 0
                      ? watchedValues.deliverables
                      : ['Entrega 1', 'Entrega 2', 'Entrega 3']}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </form>
    </div>
  );
};

export default ServicoForm;
